import { useState } from "react";
import { Clock, AlertTriangle, CheckCircle2, ShieldCheck, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openBlob } from "../lib/api";
import { CONTROL_STATES, SERVICE_TYPES } from "../lib/labels";
import { useLang } from "../lib/i18n";
import { UploadZone } from "./UploadZone";

export function Svc({ type }) {
  const { tr } = useLang();
  return tr(SERVICE_TYPES[type] || type);
}

const STATE_STYLE = {
  attente_rapport: ["bg-amber-50 text-amber-900 border-amber-300", Clock],
  ecart: ["bg-red-50 text-red-800 border-red-300", AlertTriangle],
  conforme: ["bg-emerald-50 text-emerald-800 border-emerald-300", CheckCircle2],
  validee: ["bg-blue-50 text-blue-800 border-blue-300", ShieldCheck],
};

export function StatusBadge({ status, testid }) {
  const { tr } = useLang();
  const [cls, Icon] = STATE_STYLE[status] || STATE_STYLE.attente_rapport;
  return (
    <span className={`tl-chip ${cls}`} data-testid={testid || `status-badge-${status}`}>
      <Icon size={12} /> {tr(CONTROL_STATES[status])}
    </span>
  );
}

export function Chip({ children, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-300", amber: "bg-amber-50 text-amber-900 border-amber-300",
    red: "bg-red-50 text-red-800 border-red-300", green: "bg-emerald-50 text-emerald-800 border-emerald-300",
    blue: "bg-blue-50 text-blue-800 border-blue-300",
  };
  return <span className={`tl-chip ${tones[tone]}`} data-testid={testid}>{children}</span>;
}

export function PageTitle({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-6 tl-fade">
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold uppercase leading-none" data-testid="page-title">{title}</h1>
        {sub && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{sub}</p>}
      </div>
      <div className="ml-auto flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export const matchQ = (q, ...vals) => !q.trim() || vals.join(" ").toLowerCase().includes(q.trim().toLowerCase());

export function SearchBox({ value, onChange, testid, className = "w-56" }) {
  const { t } = useLang();
  return (
    <div className={`relative ${className}`}>
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input type="search" className="tl-input !pl-9" placeholder={t("c.search")} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

export const focusRing = (on) => (on ? "ring-4 ring-amber-400 ring-offset-2" : "");

export function Field({ label, children, className = "" }) {
  const { tr } = useLang();
  return (
    <label className={`block ${className}`}>
      <span className="tl-label">{tr(label)}</span>
      {children}
    </label>
  );
}

export function FileUploader({ kind, files, onChange, accept, label, testid, declarationId, multiple = true }) {
  const [progress, setProgress] = useState("");
  const upload = async (list) => {
    const added = [];
    for (const [i, f] of list.entries()) {
      setProgress(`${i + 1}/${list.length}`);
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);
      if (declarationId) fd.append("declaration_id", declarationId);
      try {
        const { data } = await api.post("/files", fd);
        if (data.files) {
          added.push(...data.files);
          data.errors.forEach((m) => toast.error(m));
          toast.success(`${f.name} : ${data.files.length} fichier(s) extrait(s)`);
        } else added.push(data);
      } catch (err) {
        toast.error(`${f.name} : ${errMsg(err)}`);
      }
    }
    setProgress("");
    onChange([...(files || []), ...added]);
  };
  return (
    <div>
      <UploadZone onFiles={upload} accept={accept} label={label} testid={testid} multiple={multiple} progress={progress} />
      {(files || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
              <button type="button" className="truncate text-left underline-offset-2 hover:underline" onClick={() => openBlob(`/files/${f.id}/download`)}>{f.filename}</button>
              <button type="button" className="ml-auto text-slate-500 hover:text-red-600" aria-label="Retirer" onClick={() => onChange(files.filter((x) => x.id !== f.id))}>
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NumberInput({ value, onChange, testid, step = "1", placeholder = "Non renseigné" }) {
  return (
    <input type="number" inputMode="numeric" min="0" step={step} className="tl-input tl-mono" value={value ?? ""}
      placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  );
}
